import { useId } from 'react';

import Tooltip, { TooltipRows, TooltipTrigger } from '@/components/atoms/Tooltip';

import styles from './FeedingKpiCard.module.scss';
import tooltipStyles from './KpiTooltip.module.scss';

function NoteLines(props: { lines: string[] }) {
    return (
        <>
            {props.lines.map((line) => (
                <span key={line} className={styles.noteLine}>
                    {line}
                </span>
            ))}
        </>
    );
}

interface NoteTriggerProps {
    lines: string[];
    reasonsId: string;
}

/** The note as a tooltip trigger, described by the list beside it for a screen reader. */
function NoteTrigger(props: NoteTriggerProps) {
    return (
        <TooltipTrigger className={tooltipStyles.noteTrigger} describedBy={props.reasonsId}>
            <NoteLines lines={props.lines} />
        </TooltipTrigger>
    );
}

interface KpiCoverageNoteProps {
    /** "3 of 5 pens", one line per value the selection only partly covers. */
    lines: string[];
    /** One line per pen left out, with the reason; none when the lines say it all. */
    reasons: string[];
}

/** The card's coverage note, with the pens left out and why (IPS 5, 2.2.1). */
export function KpiCoverageNote(props: KpiCoverageNoteProps) {
    const { lines, reasons } = props;
    const reasonsId = useId();

    if (reasons.length === 0) {
        return (
            <div className={styles.note}>
                <NoteLines lines={lines} />
            </div>
        );
    }

    // The shared tooltip portals the bubble into the body, as the pen table's warning marker
    // does, so the KPI grid neither clips it nor grows around it.
    return (
        <>
            <div className={styles.note}>
                <Tooltip
                    content={
                        <TooltipRows>
                            <ul className={tooltipStyles.noteReasons}>
                                {reasons.map((reason) => (
                                    <li key={reason}>{reason}</li>
                                ))}
                            </ul>
                        </TooltipRows>
                    }
                    placement="bottom"
                    layout="fit">
                    <NoteTrigger lines={lines} reasonsId={reasonsId} />
                </Tooltip>
            </div>
            <ul id={reasonsId} className={tooltipStyles.visuallyHidden}>
                {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                ))}
            </ul>
        </>
    );
}
